import { type FC, useEffect, useMemo, useState } from 'react';
import { _isEmpty, _isNotBlank } from '@/littledash';
import { ApiService, ResponseType } from '@/support/ApiService';
import { useMountedState } from '@/support/Hooks/fetch/useMountedState';
import { DateTimeRenderFormat, DateUtils } from '@/utils/Date.utils';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import Loading from '@/components/Loading';
import { NoDataMessage } from '@/components/NoDataCard';
import Banner from '@/components/UI/Banner/Banner';
import Table from '@/components/UI/Table/Table';
import type { TanStackColumns } from '@/components/UI/Table/TableComponent.model';
import UserAvatar from '@/components/UI/UserAvatar/UserAvatar';
import { toAuditChangeRows, type AuditChangeRow } from './AuditHistory.utils';
import type { SyncConfigurationAuditList } from '../SyncConfiguration.model';

type AuditEntry = SyncConfigurationAuditList['entries'][number];

/** The server's maximum audit page size; a shorter page means there are no older entries. */
const AUDIT_PAGE_SIZE = 200;

const PLACEHOLDER = '-';

const withPlaceholder = (value: string | null): string => (_isNotBlank(value) ? value : PLACEHOLDER);

const COLUMNS: TanStackColumns<AuditChangeRow> = [
  {
    id: 'created_at',
    header: 'Timestamp (UTC)',
    accessorKey: 'createdAt',
    isVisible: true,
    size: 200,
    enableSorting: true,
    cell: ({ row: { original } }: { row: { original: AuditChangeRow } }) =>
      DateUtils.renderDateTime(original.createdAt, { timezone: 'UTC', format: DateTimeRenderFormat.ISODateTimeUTC }),
  },
  {
    id: 'user',
    header: 'User',
    accessorKey: 'actorName',
    isVisible: true,
    enableSorting: true,
    cell: ({ row: { original } }: { row: { original: AuditChangeRow } }) => (
      <UserAvatar user={{ name: original.actorName }} tooltip={original.actorName} />
    ),
  },
  {
    id: 'action',
    header: 'Action',
    accessorKey: 'action',
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'entity',
    header: 'Entity',
    accessorKey: 'entity',
    isVisible: true,
    enableSorting: true,
    cell: ({ row: { original } }: { row: { original: AuditChangeRow } }) => withPlaceholder(original.entity),
  },
  {
    id: 'attribute',
    header: 'Item',
    accessorKey: 'attribute',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditChangeRow } }) => withPlaceholder(original.attribute),
  },
  {
    id: 'old_value',
    header: 'Old value',
    accessorKey: 'oldValue',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditChangeRow } }) => withPlaceholder(original.oldValue),
  },
  {
    id: 'new_value',
    header: 'New value',
    accessorKey: 'newValue',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditChangeRow } }) => withPlaceholder(original.newValue),
  },
];

type AuditLoadState = { status: 'loading' } | { status: 'failed' } | { status: 'loaded'; entries: Array<AuditEntry> };

/** Loads every audit page, newest first; the history is small enough to page through client-side. */
const loadAllAuditEntries = async (): Promise<Array<AuditEntry> | null> => {
  const entries: Array<AuditEntry> = [];
  for (;;) {
    const response = await ApiService.call({
      endpoint: 'GET /api/v1/team/sync-configuration/audit',
      query: { limit: AUDIT_PAGE_SIZE, offset: entries.length },
      options: { onError: { toast: false, capture: true, throw: false, slug: 'sync-config-audit-load' } },
    });
    if (response.type !== ResponseType.Success) {
      return null;
    }
    const page = (response.body as SyncConfigurationAuditList).entries;
    entries.push(...page);
    if (page.length < AUDIT_PAGE_SIZE) {
      return entries;
    }
  }
};

export interface SyncConfigurationAuditModalProps {
  closeModal: () => void;
}

/**
 * Modal listing the team's sync configuration audit history, one row per changed attribute in the
 * study audit log's layout (timestamp, user, action, item, old and new value).
 */
const SyncConfigurationAuditModal: FC<SyncConfigurationAuditModalProps> = ({ closeModal }) => {
  const [loadState, setLoadState] = useState<AuditLoadState>({ status: 'loading' });
  const isMounted = useMountedState();

  useEffect(() => {
    loadAllAuditEntries()
      .catch(() => null)
      .then((entries) => {
        if (isMounted()) {
          setLoadState(entries === null ? { status: 'failed' } : { status: 'loaded', entries });
        }
      });
  }, [isMounted]);

  const rows = useMemo(() => (loadState.status === 'loaded' ? toAuditChangeRows(loadState.entries) : []), [loadState]);

  return (
    <ModalContainer size="wide" testKey="sync-configuration-audit-modal">
      <ModalHeader
        title="Configuration Audit History"
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
      />

      <div className="pa3" data-test-component="SyncConfigurationAuditModal" data-testid="sync-configuration-audit">
        {loadState.status === 'loading' && <Loading txt="Loading audit history..." />}
        {loadState.status === 'failed' && (
          <Banner critical dismiss={false} testId="sync-configuration-audit__error">
            <p className="lh-copy f6 near-black ma0">Failed to load audit history. Please try again later.</p>
          </Banner>
        )}
        {loadState.status === 'loaded' && _isEmpty(rows) && <NoDataMessage title="No configuration changes yet" />}
        {loadState.status === 'loaded' && !_isEmpty(rows) && (
          <Table<AuditChangeRow>
            columns={COLUMNS}
            data={rows}
            pagination={true}
            initPageSize={25}
            sortBy={[{ id: 'created_at', desc: true }]}
          />
        )}
      </div>

      <div className="pa3 bt b--moon-gray">
        <ModalActions onCancel={closeModal} cancelBtnText="Close" />
      </div>
    </ModalContainer>
  );
};

export default SyncConfigurationAuditModal;
